import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getProduct, listLotes, listMovimientos } from '../services/productsService';
import { formatDateTime, formatMoney } from '../lib/format';
import { friendlyError } from '../lib/errors';
import LoadingState from '../components/ui/LoadingState';
import ErrorState from '../components/ui/ErrorState';
import EmptyState from '../components/ui/EmptyState';
import Badge from '../components/ui/Badge';

export default function ProductDetailPage() {
  const { uid } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [lotes, setLotes] = useState([]);
  const [movimientos, setMovimientos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const p = await getProduct(uid);
      setProduct(p);
      // Lotes y movimientos SOLO al abrir el detalle (principio lazy)
      const [lotesData, movs] = await Promise.all([
        p.controlaLotes ? listLotes(uid) : Promise.resolve([]),
        listMovimientos({ ruc: p.ruc, productUid: uid, pageSize: 10 }),
      ]);
      setLotes(lotesData);
      setMovimientos(movs);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <LoadingState label="Cargando producto…" />;
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!product) return <EmptyState title="Producto no encontrado" />;

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <p className="breadcrumb">
            <Link to="/admin/productos">Productos</Link> / {product.name}
          </p>
          <h1>{product.name}</h1>
          <p className="muted">{product.descripcion || 'Sin descripción'}</p>
        </div>
        <div className="row-actions">
          <button type="button" className="btn btn-ghost" onClick={() => navigate(-1)}>Volver</button>
          <Link className="btn btn-primary" to={`/admin/productos/${uid}/editar`}>Editar</Link>
        </div>
      </header>

      <section className="stat-grid compact">
        <div className="stat-card"><p className="stat-label">Código</p><p className="stat-value">{product.id || '—'}</p></div>
        <div className="stat-card"><p className="stat-label">Precio</p><p className="stat-value">{formatMoney(product.price, product.currency)}</p></div>
        <div className="stat-card"><p className="stat-label">Costo</p><p className="stat-value">{formatMoney(product.costo, product.currency)}</p></div>
        <div className="stat-card"><p className="stat-label">Stock</p><p className="stat-value">{product.controlaStock ? product.stock ?? 0 : '—'}</p></div>
        <div className="stat-card">
          <p className="stat-label">Estado</p>
          <p className="stat-value">
            {product.activo === false ? <Badge tone="muted">Inactivo</Badge> : <Badge tone="ok">Activo</Badge>}
          </p>
        </div>
      </section>

      <section className="meta-grid">
        <div><span className="muted">Marca</span><div>{product.marca || '—'}</div></div>
        <div><span className="muted">Modelo</span><div>{product.modelo || '—'}</div></div>
        <div><span className="muted">Código barra</span><div>{product.codigoBarra || '—'}</div></div>
        <div><span className="muted">Unidad</span><div>{product.unidad || '—'}</div></div>
        <div><span className="muted">Impuesto</span><div>{product.impuesto ?? '—'}</div></div>
        <div>
          <span className="muted">Control</span>
          <div>
            {product.controlaStock ? 'Stock ' : ''}
            {product.controlaLotes ? '· Lotes' : ''}
            {!product.controlaStock && !product.controlaLotes ? 'Sin control' : ''}
          </div>
        </div>
      </section>

      <section className="panel">
        <header className="panel-header">
          <h2>Lotes</h2>
          {product.controlaLotes ? <span className="muted">FEFO se aplica al descontar stock</span> : null}
        </header>
        {!product.controlaLotes ? (
          <EmptyState title="Este producto no controla lotes" />
        ) : lotes.length === 0 ? (
          <EmptyState title="Sin lotes registrados" />
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Lote</th>
                  <th>Cantidad</th>
                  <th>Vencimiento</th>
                </tr>
              </thead>
              <tbody>
                {lotes.map((l) => {
                  const fecha = l.fechaCaducidad?.toDate
                    ? l.fechaCaducidad.toDate()
                    : l.fechaCaducidad
                      ? new Date(l.fechaCaducidad)
                      : null;
                  const vencido = fecha && fecha < new Date();
                  return (
                    <tr key={l.id}>
                      <td>{l.numeroLote || '—'}</td>
                      <td>{l.cantidad ?? 0}</td>
                      <td>
                        {fecha ? fecha.toLocaleDateString('es-PE') : '—'}
                        {vencido ? <Badge tone="warn"> Vencido</Badge> : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="panel">
        <header className="panel-header">
          <h2>Movimientos de stock</h2>
          <span className="muted">últimos 10</span>
        </header>
        {movimientos.length === 0 ? (
          <EmptyState title="Sin movimientos" />
        ) : (
          <ul className="simple-list">
            {movimientos.map((m) => (
              <li key={m.id}>
                <div>
                  <strong>{m.tipo}</strong>
                  <div className="muted">{formatDateTime(m.timestamp)}</div>
                </div>
                <Badge tone={String(m.tipo).toLowerCase().includes('venta') || String(m.tipo).toLowerCase().includes('redu') ? 'warn' : 'ok'}>
                  {m.cantidad > 0 && !String(m.tipo).toLowerCase().includes('venta') ? `+${m.cantidad}` : m.cantidad}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
